import { BookMarked } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { CreateCatalogRequest } from "@/lib/api/catalog";
import { cn } from "@/lib/utils";
import { JournalContributorsFields } from "@/components/catalog/JournalContributorsFields";
import { JournalTextField } from "@/components/catalog/JournalFieldParts";
import { RequiredLabel } from "@/components/catalog/RequiredLabel";

type Biblio = CreateCatalogRequest["biblio"];

interface JournalMetadataFieldsProps {
  value: Biblio;
  onChange: (value: Biblio) => void;
  errors: Record<string, string>;
}

export function JournalMetadataFields({ value, onChange, errors }: JournalMetadataFieldsProps) {
  const update = <K extends keyof Biblio>(key: K, next: Biblio[K]) =>
    onChange({ ...value, [key]: next });
  return (
    <Card className="overflow-hidden bg-white shadow-sm">
      <div className="space-y-5 p-6">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <BookMarked className="h-5 w-5 text-primary" />
          Journal information
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <RequiredLabel htmlFor="journal-title" required>
              Title
            </RequiredLabel>
            <Input
              id="journal-title"
              value={value.title}
              onChange={(event) => update("title", event.target.value)}
              placeholder="Enter the journal title"
              className={cn("mt-1.5", errors.title && "border-red-500")}
              aria-invalid={Boolean(errors.title)}
              aria-describedby={errors.title ? "journal-title-error" : undefined}
            />
            {errors.title ? (
              <p id="journal-title-error" className="mt-1 text-sm text-red-600">
                {errors.title}
              </p>
            ) : null}
          </div>
          <JournalTextField
            id="journal-subtitle"
            label="Subtitle"
            value={value.subtitle}
            onChange={(next) => update("subtitle", next)}
            placeholder="e.g., A Modern Approach"
          />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <JournalTextField
            id="journal-form-issn"
            label="ISSN"
            required
            value={value.issn}
            onChange={(next) => update("issn", next)}
            placeholder="ISSN e.g. 0028-0836"
            invalid={Boolean(errors.issn)}
            describedBy={errors.issn ? "journal-form-issn-error" : undefined}
          />
          <JournalTextField
            id="journal-form-eissn"
            label="eISSN"
            value={value.eIssn}
            onChange={(next) => update("eIssn", next)}
            placeholder="eISSN e.g. 1476-4687"
          />
          <JournalTextField
            id="journal-form-issnl"
            label="ISSN-L"
            value={value.issnL}
            onChange={(next) => update("issnL", next)}
            placeholder="ISSN-L e.g. 0028-0836"
          />
        </div>
        {errors.issn ? (
          <p id="journal-form-issn-error" className="text-sm text-red-600">
            {errors.issn}
          </p>
        ) : null}
        <div className="grid gap-4 md:grid-cols-2">
          <JournalTextField
            id="journal-doi"
            label="DOI"
            value={value.doi}
            onChange={(next) => update("doi", next)}
            placeholder="Digital Object Identifier"
          />
          <JournalTextField
            id="journal-website"
            label="Website Link"
            value={value.websiteUrl}
            onChange={(next) => update("websiteUrl", next)}
            placeholder="https://example.com/journal (optional)"
            invalid={Boolean(errors.websiteUrl)}
            describedBy={errors.websiteUrl ? "journal-website-error" : undefined}
          />
        </div>
        {errors.websiteUrl ? (
          <p id="journal-website-error" className="text-sm text-red-600">
            {errors.websiteUrl}
          </p>
        ) : null}
        <JournalContributorsFields value={value} onChange={onChange} errors={errors} />
        <div className="grid gap-4 md:grid-cols-3">
          <JournalTextField
            id="journal-publisher"
            label="Publisher"
            value={value.publisher}
            onChange={(next) => update("publisher", next)}
            placeholder="Publisher name"
          />
          <JournalTextField
            id="journal-place"
            label="Place of publication"
            value={value.placeOfPublication}
            onChange={(next) => update("placeOfPublication", next)}
            placeholder="e.g., Lagos, New York"
          />
          <JournalTextField
            id="journal-call-number"
            label="Call number"
            value={value.callNumber}
            onChange={(next) => update("callNumber", next.toUpperCase())}
            placeholder="e.g., PER 500 or QH301.N2"
          />
        </div>
        <div>
          <RequiredLabel htmlFor="journal-description">Description</RequiredLabel>
          <Textarea
            id="journal-description"
            value={value.description ?? ""}
            onChange={(event) => update("description", event.target.value)}
            placeholder="Enter a brief description or summary"
            className="mt-1.5"
          />
        </div>
      </div>
    </Card>
  );
}
